import '@fontsource/lexend/300.css'
import '@fontsource/lexend/400.css'
import '@fontsource/lexend/500.css'
import '@fontsource/lexend/600.css'
import '@fontsource/lexend/700.css'
import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'

import { createApp } from 'vue'
import { EventType } from '@azure/msal-browser'
import { registerSW } from 'virtual:pwa-register'
import App from './App.vue'
import { msalInstance } from './auth/msalConfig'
import { getMsalState, msalPlugin, refreshPermissionsAsync } from './auth/msalPlugin'
import { RouterNavigationClient } from './auth/navigationClient'
import { initSentry } from './plugins/sentry'
import { vuetify } from './plugins/vuetify'
import router from './router'
import { appStore, initializeConnectivity } from './store/appStore'
import { replayOutboxAsync } from './services/offlineService'
import './styles/main.scss'

const updateServiceWorker = registerSW({
  immediate: true,
  onNeedRefresh () {
    appStore.updateAvailable = true
  },
  onOfflineReady () {
    appStore.offlineReady = true
  },
})

appStore.updateServiceWorker = () => updateServiceWorker(true)

initializeConnectivity()

function syncOfflineMutations () {
  replayOutboxAsync().catch((error) => {
    console.error('Failed to sync offline mutations.', error)
    appStore.syncError = error
  })
}

async function refreshOnlineStateAsync () {
  try {
    await refreshPermissionsAsync(getMsalState())
  } catch (error) {
    console.error('Failed to refresh user permissions after reconnecting.', error)
  }

  syncOfflineMutations()
}

async function bootstrapAsync () {
  msalInstance.setNavigationClient(new RouterNavigationClient(router))

  // MSAL must be initialized, and any pending redirect response handled,
  // before the router resolves its first navigation.
  await msalInstance.initialize()
  const redirectResult = await msalInstance.handleRedirectPromise().catch((error) => {
    console.error(error)
    return null
  })

  if (redirectResult?.account) {
    msalInstance.setActiveAccount(redirectResult.account)
  } else {
    const [firstAccount] = msalInstance.getAllAccounts()
    if (firstAccount) {
      msalInstance.setActiveAccount(firstAccount)
    }
  }

  msalInstance.addEventCallback((event) => {
    if ([EventType.LOGIN_SUCCESS, EventType.SSO_SILENT_SUCCESS].includes(event.eventType) && event.payload?.account) {
      msalInstance.setActiveAccount(event.payload.account)
    }
  })

  const app = createApp(App)
    .provide('appStore', appStore)
    .use(msalPlugin, msalInstance)

  initSentry(app)
  await refreshPermissionsAsync(getMsalState())

  app
    .use(router)
    .use(vuetify)
    .mount('#app')

  window.addEventListener('online', refreshOnlineStateAsync)
  syncOfflineMutations()
}

bootstrapAsync()
